import React, { useEffect, useState } from 'react';
import {
    useTable,
    useSortBy,
    usePagination,
    useExpanded,
    useGlobalFilter,
    Column,
    SortingRule,
} from 'react-table';
import { Col, Container, Row } from 'react-bootstrap';
import _ from 'lodash';
import PaginationButtonGroups from './PaginationButtonGroups';
import { DataTableProps } from './DataTable';
import './ReactTableStyle.css';
import { generateSortingIndicator } from '../listUtils';
import Spinner from '../Spinner';
import usePrevious from '../../../hooks/usePrevious';
import { initialStateType } from './DataGrid';
import { DEFAULT_INITIAL_STATE } from '../../../utils/Constants';

export interface SearchObjectType {
    [key: string]: string | string[] | boolean | number | null;
}

interface DataGridControlledProps<T extends object> {
    columns: Array<Column<T>>;
    searchString: string;
    setSearching: Function;
    setResponseError: Function;
    fetchData: Function;
    fetchDataCount: Function;
    renderRowSubComponent: Function;
    initialDataSize: number;
    setInitialDataSize: Function;
    initialState?: initialStateType;
    setInitialState?: Function;
}
interface DataGridControlledCoreProps<T extends object> extends DataTableProps<T> {
    fetchData: Function;
    searchString: string;
    controlledPageCount: number;
    loading?: boolean;
    initialState?: initialStateType;
    setInitialState?: Function;
}

const DataGridControlled = <T extends object>(props: DataGridControlledProps<T>) => {
    const {
        columns,
        fetchData,
        fetchDataCount,
        renderRowSubComponent,
        initialDataSize,
        setInitialDataSize,
        initialState,
        setInitialState,
        setSearching,
        setResponseError,
    } = props;
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [controlledPageCount, setControlledPageCount] = useState(0);

    const fetchDataInside = async ({
        pageSize,
        pageIndex,
        sortBy,
        searchString,
        searchChange,
    }: {
        pageSize: number;
        pageIndex: number;
        sortBy: SortingRule<Object>[];
        searchString: string;
        searchChange: boolean;
    }) => {
        setLoading(true);

        const search = JSON.parse(searchString);
        try {
            if (initialDataSize === -1 || searchChange) {
                const [dataTotalCount, newData] = await Promise.all([
                    fetchDataCount(search),
                    fetchData({ pageSize, pageIndex, sortBy, search }),
                ]);
                setControlledPageCount(Math.ceil(dataTotalCount / pageSize));
                setInitialDataSize(dataTotalCount.toString());
                setData(newData);
            } else {
                // no need to get the data size again if no search change
                setControlledPageCount(Math.ceil(initialDataSize / pageSize));
                const newData = await fetchData({ pageSize, pageIndex, sortBy, search });
                setData(newData);
            }
            setResponseError('');
        } catch (e) {
            if (e instanceof Error) {
                setResponseError(e.message);
            }
            setData([]);
        } finally {
            setLoading(false);
            setSearching(false);
        }
    };

    return (
        <DataGridControlledCore<T>
            columns={columns}
            data={data}
            searchString={props.searchString} // only this one use "props.", to avoid duplicated names
            fetchData={fetchDataInside}
            controlledPageCount={controlledPageCount}
            initialState={initialState}
            setInitialState={setInitialState}
            loading={loading}
            renderRowSubComponent={renderRowSubComponent}
        />
    );
};

const DataGridControlledCore = <T extends object>({
    columns,
    data,
    searchString,
    initialState,
    setInitialState,
    fetchData,
    controlledPageCount,
    loading,
    renderRowSubComponent,
}: DataGridControlledCoreProps<T>) => {
    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page,
        canPreviousPage,
        canNextPage,
        pageOptions,
        pageCount,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        state: { pageIndex, pageSize, sortBy },
    } = useTable(
        {
            columns,
            data,
            initialState: initialState || DEFAULT_INITIAL_STATE,
            manualPagination: true, // Tell the usePagination
            // hook that we'll handle our own data fetching
            // This means we'll also have to provide our own
            // pageCount.
            pageCount: controlledPageCount,
            autoResetSortBy: false,
            autoResetPage: false,
        },
        useGlobalFilter,
        useSortBy,
        useExpanded,
        usePagination,
    );

    const previousSearch = usePrevious(searchString);
    const previousState = usePrevious(initialState);

    useEffect(() => {
        const newState = {
            pageIndex,
            pageSize,
            sortBy,
        };

        if (!_.isEqual(previousState, newState) && setInitialState) {
            setInitialState(JSON.stringify(newState));
        }

        if (previousSearch !== searchString) {
            // reset to the first page for a new search
            fetchData({ pageIndex: 0, pageSize, sortBy, searchString, searchChange: true });
            gotoPage(0); // To trick react table's state go back to first page
        } else {
            fetchData({ pageIndex, pageSize, sortBy, searchString, searchChange: false });
        }
    }, [searchString, pageIndex, pageSize, sortBy]);

    return loading ? (
        <Spinner />
    ) : (
        <>
            <Container fluid {...getTableProps()}>
                {headerGroups.map((headerGroup) => (
                    <Row
                        {...headerGroup.getHeaderGroupProps()}
                        className="heading d-none d-xl-flex"
                    >
                        {headerGroup.headers.map((column, index) => (
                            <Col
                                {...column.getHeaderProps(column.getSortByToggleProps())}
                                className={`${(column as any).customClass}`}
                                xs={(column as any).xs}
                                sm={(column as any).sm}
                                xl={(column as any).xl}
                            >
                                {column.render('Header')}{' '}
                                {column.canSort ? (
                                    <span>
                                        {index === 0
                                            ? ''
                                            : generateSortingIndicator(
                                                  column.isSorted,
                                                  column.isSortedDesc,
                                              )}
                                    </span>
                                ) : null}
                            </Col>
                        ))}
                    </Row>
                ))}

                <div {...getTableBodyProps()}>
                    {page.map((row) => {
                        prepareRow(row);
                        return (
                            <React.Fragment key={row.index}>
                                <Row {...row.getRowProps({})} className="row card-style">
                                    {row.cells.map((cell) => (
                                        <Col
                                            xs={(cell.column as any).xs}
                                            sm={(cell.column as any).sm}
                                            xl={(cell.column as any).xl}
                                            {...cell.getCellProps({
                                                className: ` ${(cell.column as any).customClass}`,
                                            })}
                                        >
                                            {cell.render('Cell')}
                                        </Col>
                                    ))}
                                </Row>
                                {row.isExpanded && renderRowSubComponent ? (
                                    <>{renderRowSubComponent({ row })}</>
                                ) : null}
                            </React.Fragment>
                        );
                    })}
                    {page.length <= 0 && (
                        <Row className="row card-style">
                            <Col xs="12" className="text-center">
                                <i>No data to display.</i>
                            </Col>
                        </Row>
                    )}
                </div>
            </Container>

            <PaginationButtonGroups
                canPreviousPage={canPreviousPage}
                canNextPage={canNextPage}
                pageCount={pageCount}
                pageOptions={pageOptions}
                pageIndex={pageIndex}
                gotoPage={gotoPage}
                previousPage={previousPage}
                nextPage={nextPage}
                pageSize={pageSize}
                setPageSize={setPageSize}
            />
        </>
    );
};

export default DataGridControlled;
